/**
 * V70 Tournament — 본인 접근 가능 여부 조회 hook
 *
 * - BE `getMyTournamentAccess` callable 호출
 * - 설정 변경 시 refetch() 수동 호출로 즉시 반영 (사용자 지시 §4)
 *
 * V70-H — 사이드바·탭 가드가 결과를 공유 (게임대회 탭 이동마다 가드가 재조회하며 페이지 표시를 막던 직렬 대기 제거)
 *   - 같은 사용자(UID)의 성공 결과를 메모리에 보관: 30초 이내 재진입은 호출 없음,
 *     지났으면 보관 결과로 먼저 표시하고 뒤에서 다시 확인 → 결과가 바뀌면 사이드바·가드 모두 갱신
 *   - 동시 요청 1회로 합침 (앱 첫 로드 시 사이드바 + 가드 중복 호출 제거)
 *   - 조회 실패는 보관하지 않고 기존과 같이 OFF 처리
 *   - 판정 기준·서버 권한 검사는 그대로 (각 게임대회 Callable 이 서버에서 다시 검사)
 */
import { useEffect, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { auth } from '../../firebase/config';
import {
  fetchQuery,
  isQueryFresh,
  peekQuery,
  subscribeQuery,
  TOURNAMENT_QUERY_FRESH_MS,
} from '../../pages/tournament/shared/tournamentQueryCache';

interface AccessState {
  isLoading: boolean;
  accessible: boolean;
  reason?: string | null;
  refetch: () => Promise<void>;
}

interface AccessResult { accessible: boolean; reason: string | null }

function accessKey(): string | null {
  const uid = (auth as { currentUser?: { uid?: string } | null } | null | undefined)?.currentUser?.uid;
  return uid ? `access:${uid}` : null;
}

async function callAccess(): Promise<AccessResult> {
  const functions = getFunctions();
  if (!functions) throw new Error('functions 초기화 실패');
  const fn = httpsCallable<{}, { success: boolean; accessible: boolean; reason?: string | null }>(
    functions,
    'getMyTournamentAccess'
  );
  const r = await fn({});
  return { accessible: r.data?.accessible === true, reason: r.data?.reason || null };
}

export function useMyTournamentAccess(): AccessState {
  const key = accessKey();
  const cached = key ? peekQuery<AccessResult>(key) : undefined;
  const [isLoading, setIsLoading] = useState(!cached);
  const [accessible, setAccessible] = useState(cached?.data.accessible ?? false);
  const [reason, setReason] = useState<string | null>(cached?.data.reason ?? null);

  const refetch = useCallback(async (force = true) => {
    const k = accessKey();
    try {
      // UID 를 알 수 없으면(초기화 전) 보관 없이 매번 조회 — 기존 동작
      const r = k ? await fetchQuery(k, callAccess, { force }) : await callAccess();
      setAccessible(r.accessible);
      setReason(r.reason);
    } catch (err: any) {
      // 설정 조회 실패 → OFF 처리 (사용자 지시 §4)
      setAccessible(false);
      setReason(err?.message || '접근 확인 실패');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!key) return undefined;
    // 다른 화면(사이드바/가드)에서 다시 확인한 결과를 즉시 반영
    return subscribeQuery(key, () => {
      const e = peekQuery<AccessResult>(key);
      if (!e) return;
      setAccessible(e.data.accessible);
      setReason(e.data.reason);
      setIsLoading(false);
    });
  }, [key]);

  useEffect(() => {
    if (key && isQueryFresh(key, TOURNAMENT_QUERY_FRESH_MS)) return;
    refetch(false);
  }, [key, refetch]);

  return { isLoading, accessible, reason, refetch: () => refetch(true) };
}
